import { formatPrice } from "./utility";

/* ================= BUSINESS HELPERS ================= */

const formatClientSolde = (totalDebit, totalCredit) => {
  const debit = Number(totalDebit || 0);
  const credit = Number(totalCredit || 0);

  // RÈGLE MÉTIER : crédit - débit
  const solde = credit - debit;

  const sign = solde > 0 ? '+' : solde < 0 ? '-' : '';
  return `${sign}${formatPrice(Math.abs(solde), false)}`;
};

const getModeReglementLabel = (mode) => {
  switch (mode) {
    case 'ESPECES': return 'Especes';
    case 'CARTE_BANCAIRE': return 'Carte bancaire';
    case 'CHEQUE': return 'Cheque';
    case 'VIREMENT': return 'Virement';
    case 'TRAITE': return 'Traite';
    default: return mode || '-';
  }
};

/* ================= ESC/POS CONSTANTS ================= */

export const ESC = {
  INIT: '\x1B\x40',
  BOLD_ON: '\x1B\x45\x01',
  BOLD_OFF: '\x1B\x45\x00',
  CENTER: '\x1B\x61\x01',
  LEFT: '\x1B\x61\x00',
  CUT: '\x1D\x56\x00',

  // 🔥 Western Europe (French-safe)
  CODEPAGE_CP850: '\x1B\x74\x02',
};

/* ================= TEXT NORMALIZATION ================= */

const normalizeText = (text = '') => {
  return String(text)
    .normalize('NFD')                 // split accents
    .replace(/[\u0300-\u036f]/g, '')  // remove accents
    .replace(/ß/g, 'ss')
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .replace(/€/g, 'EUR');
};

/* ================= RECEIPT WIDTH ================= */

// 80mm thermal printer (ZKTeco 8005)
// Normal font ≈ 48 chars
const RECEIPT_WIDTH = 48;

/* ================= HELPERS ================= */

const line = () => '-'.repeat(RECEIPT_WIDTH) + '\n';

const center = (text) => {
  const safe = normalizeText(text);
  const pad = Math.max(
    0,
    Math.floor((RECEIPT_WIDTH - safe.length) / 2)
  );
  return ' '.repeat(pad) + safe + '\n';
};

const padRight = (text, len) =>
  (normalizeText(text) + ' '.repeat(len)).substring(0, len);

const padLeft = (text, len) =>
  (' '.repeat(len) + normalizeText(text)).slice(-len);

/* ================= MAIN BUILDER ================= */

export const buildEscPosReceipt = ({
  businessName,
  address,
  telephone,
  receiptData,
}) => {
  let t = '';

  /* ===== INIT ===== */
  t += ESC.INIT;
  t += ESC.CODEPAGE_CP850; // 🔥 critical

  /* ===== HEADER ===== */
  t += ESC.CENTER + ESC.BOLD_ON;
  t += center(businessName || 'MATJARPOS');
  t += ESC.BOLD_OFF;

  if (address) {
    normalizeText(address)
      .split('\n')
      .forEach(l => (t += center(l)));
  }

  if (telephone) {
    t += center(telephone);
  }

  t += line();

  /* ===== INFO ===== */
  t += ESC.CENTER + ESC.BOLD_ON;
  t += center('RECU DE VENTE');
  t += ESC.BOLD_OFF;

  t += ESC.LEFT;
  t += `N° : ${normalizeText(receiptData?.numero || '-')}\n`;
  t += `Date : ${normalizeText(receiptData?.createdAt || '-')}\n`;
  t += `Vendeur : ${normalizeText(receiptData?.createdBy || '---')}\n`;
  t += `Client  : ${normalizeText(receiptData?.clientNom || 'Passager')}\n`;

  t += line();

  /* ===== ARTICLES HEADER ===== */
  // 24 + 6 + 9 + 9 = 48
  t += ESC.BOLD_ON;
  t +=
    padRight('Article', 24) +
    padLeft('Qte', 6) +
    padLeft('PU', 9) +
    padLeft('Total', 9) +
    '\n';
  t += ESC.BOLD_OFF;
  t += line();

  /* ===== ARTICLES LINES ===== */
  receiptData?.lignes?.forEach(item => {
    const rawName = normalizeText(item.articleNom || '');
    const name =
      rawName.length > 24
        ? rawName.slice(0, 23) + '.'
        : rawName;

    const qte = String(Number(item.quantiteLivree || 0));
    const pu = formatPrice(item.prixUnitaireTTC || 0, false);
    const total = formatPrice(item.montantTTC || 0, false);

    t +=
      padRight(name, 24) +
      padLeft(qte, 6) +
      padLeft(pu, 9) +
      padLeft(total, 9) +
      '\n';
  });

  t += line();

  /* ===== TOTAL ===== */
  t += ESC.BOLD_ON;
  t +=
    padRight('TOTAL TTC', 30) +
    padLeft(
      formatPrice(receiptData?.montantTotalTTC || 0, false),
      18
    ) +
    '\n';
  t += ESC.BOLD_OFF;

  t += line();

  /* ===== PAYMENT ===== */
  t += `Mode paiement : ${normalizeText(
    getModeReglementLabel(receiptData?.modeReglement)
  )}\n`;

  t +=
    padRight('Montant paye :', 30) +
    padLeft(
      formatPrice(receiptData?.montantPaye || 0, false),
      18
    ) +
    '\n';

  if (
    receiptData?.modeReglement === 'ESPECES' &&
    receiptData?.montantPaye > receiptData?.montantTotalTTC
  ) {
    t +=
      padRight('Monnaie :', 30) +
      padLeft(
        formatPrice(
          receiptData.montantPaye -
            receiptData.montantTotalTTC,
          false
        ),
        18
      ) +
      '\n';
  }

  t += line();

  /* ===== CLIENT BALANCE ===== */
  if (
    !receiptData?.isComptoir &&
    receiptData?.clientTotalDebit &&
    receiptData?.clientTotalCredit
  ) {
    t += ESC.BOLD_ON;
    t += padRight('SOLDE CLIENT', RECEIPT_WIDTH) + '\n';
    t += ESC.BOLD_OFF;

    t +=
      padLeft(
        formatClientSolde(
          receiptData.clientTotalDebit,
          receiptData.clientTotalCredit
        ),
        RECEIPT_WIDTH
      ) +
      '\n';
  }

  /* ===== FOOTER ===== */
  t += '\n';
  t += ESC.CENTER;
  t += 'Merci de votre confiance\n';
  t += 'Powered by MatjarPOS\n\n';

  /* ===== CUT ===== */
  t += ESC.CUT;

  return t;
};
